import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useNavigationGuard } from './StepValidationContext'
import type { LibraTicket } from './ticket/libraTicketsApi'
import type { LibraWorkflowStep } from './workflow/workflowStepsApi'
import { useWorkflow } from './WorkflowContext'

interface StepNavigationContextType {
  activeStepIndex: number
  setActiveStepIndex: (index: number) => void
  activeStep: LibraWorkflowStep | undefined
  maxNavigableIndex: number
  navigateToStep: (stepId: string) => void
}

const StepNavigationContext = createContext<
  StepNavigationContextType | undefined
>(undefined)

export const StepNavigationProvider: FC<{
  ticket: LibraTicket
  children: ReactNode
}> = ({ ticket, children }) => {
  const workflow = useWorkflow()
  const { guardNavigation } = useNavigationGuard()

  const maxNavigableIndex = useMemo(
    () => workflow.stepIndices.get(ticket.stepId) ?? 0,
    [ticket.stepId, workflow.stepIndices]
  )

  const [activeStepIndex, setActiveStepIndexRaw] =
    useState<number>(maxNavigableIndex)

  useEffect(() => {
    setActiveStepIndexRaw(maxNavigableIndex)
  }, [maxNavigableIndex])

  const activeStep = workflow.steps[activeStepIndex]

  const setActiveStepIndex = useCallback(
    (index: number) => {
      guardNavigation(() => setActiveStepIndexRaw(index))
    },
    [guardNavigation]
  )

  const navigateToStep = useCallback(
    (stepId: string) => {
      const index = workflow.stepIndices.get(stepId)
      if (index !== undefined) {
        guardNavigation(() => setActiveStepIndexRaw(index))
      }
    },
    [workflow.stepIndices, guardNavigation]
  )

  const value = useMemo<StepNavigationContextType>(
    () => ({
      activeStepIndex,
      setActiveStepIndex,
      activeStep,
      maxNavigableIndex,
      navigateToStep,
    }),
    [
      activeStepIndex,
      setActiveStepIndex,
      activeStep,
      maxNavigableIndex,
      navigateToStep,
    ]
  )

  return (
    <StepNavigationContext.Provider value={value}>
      {children}
    </StepNavigationContext.Provider>
  )
}

export function useStepNavigation(): StepNavigationContextType {
  const ctx = useContext(StepNavigationContext)
  if (!ctx) {
    throw new Error(
      'useStepNavigation must be used within a StepNavigationProvider'
    )
  }
  return ctx
}
